import { FC, useState } from 'react';
import { Contact, Development, LegalPerson, Session } from '~/api/types';
import { OtpSendingScreen } from '../screens/otp-sending';
import { DevelopmentSelectionScreen } from '../screens/development-selection';
import { LegalPersonSelectionScreen } from '../screens/legal-person-selection';
import { OtpVerificationScreen } from '../screens/otp-verification';
import config from '~/config';
import { queryLegalPerson, resendOtp, verifyOtp } from '~/api';
import { sendOtp } from '~/api';

export const MagicLinkWizard: FC<MagicLinkWizardProps> = ({ session, clearSession, refetchSession }) => {
  /**
   * Currently, we support one legal person per session, so we skip the legal person
   * selection step for valid sessions and set the first legal person as the selected one.
   */
  const [step, setStep] = useState<Step>(session ? 'development-selection' : 'legal-person-selection');
  const [selectedLegalPerson, setSelectedLegalPerson] = useState<LegalPerson | null>(session?.legalPersons[0] ?? null);
  const [selectedDevelopment, setSelectedDevelopment] = useState<Development | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [otpId, setOtpId] = useState<string | null>(null);

  const CURRENT_CUB_MAGIC_LINK = window.location.host;
  const IS_WHITE_LABEL = config.CUB_HOST !== CURRENT_CUB_MAGIC_LINK;

  const onLegalPersonSelection = (legalPerson: LegalPerson) => {
    setSelectedLegalPerson(legalPerson);

    const filteredDevelopments = legalPerson.developments.filter((d) => {
      // always show all contracts for non-white-label deployments
      if (!IS_WHITE_LABEL) return true;

      // show only contracts allowed of current magic link white-label
      const sanitizedMagicLinkUrl = d.magicLinkUrl?.replace('https://', '').replace('/', '');
      if (sanitizedMagicLinkUrl === CURRENT_CUB_MAGIC_LINK) return true;

      return false;
    });

    if (filteredDevelopments.length === 1 && !filteredDevelopments[0].customerPortalUrl) {
      // we can skip the development selection directly to the contact selection for otp delivery step
      setSelectedDevelopment(filteredDevelopments[0]);
      setStep('otp-sending');
    } else {
      setStep('development-selection');
    }
  };

  const onDevelopmentSelection = (customer: Development) => {
    setSelectedDevelopment(customer);
    setStep('otp-sending');
  };

  const onContactSelection = (contact: Contact, otpId: string) => {
    setSelectedContact(contact);
    setOtpId(otpId);
    setStep('otp-verification');
  };

  /**
   * Currently, we support just one legal person per session. So, in order to
   * change the legal person, we need to clear the session and start over.
   */
  const goBackFromDevelopmentSelection = () => {
    clearSession();
  };

  const goBackFromOtpSending = () => {
    setSelectedDevelopment(null);
    setStep('development-selection');
  };

  /**
   * After redirecting the user to the customer portal on otp confirmation,
   * we reset the wizard state by refetching the session with the newly verified
   * customer portal url for the development. By doing so, the user may access
   * the customer portal for this development again directly from the development
   * selection screen without going through the otp confirmation step again and
   * may also access the customer portal for other developments by going through
   * the otp confirmation step for these developments.
   */
  const onOtpConfirmation = () => refetchSession();

  switch (step) {
    case 'legal-person-selection': {
      return (
        <LegalPersonSelectionScreen
          title="Solicitar acesso"
          description="Por favor, digite seu CPF ou CNPJ registrado no contrato para solicitar acesso a sua Área do Cliente"
          buttonText="Solicitar acesso"
          queryLegalPerson={queryLegalPerson}
          onLegalPersonSelection={onLegalPersonSelection}
        />
      );
    }
    case 'development-selection': {
      if (!selectedLegalPerson) return null;
      return (
        <DevelopmentSelectionScreen
          title="O que deseja acessar?"
          person={selectedLegalPerson}
          onDevelopmentSelection={onDevelopmentSelection}
          goBack={goBackFromDevelopmentSelection}
        />
      );
    }
    case 'otp-sending': {
      if (!selectedLegalPerson || !selectedDevelopment) return null;
      return (
        <OtpSendingScreen
          person={selectedLegalPerson}
          development={selectedDevelopment}
          onOtpSent={onContactSelection}
          goBack={goBackFromOtpSending}
          sendOtp={sendOtp}
        />
      );
    }
    case 'otp-verification': {
      if (!selectedContact || !otpId) return null;
      return (
        <OtpVerificationScreen
          buttonText="Acessar área do cliente"
          contact={selectedContact}
          otpId={otpId}
          onOtpConfirmation={onOtpConfirmation}
          resendOtp={resendOtp}
          verifyOtp={verifyOtp}
          showRememberMeOption={true}
        />
      );
    }
  }
};

type Step = 'legal-person-selection' | 'development-selection' | 'otp-sending' | 'otp-verification';

interface MagicLinkWizardProps {
  session: Session | null;
  clearSession: () => void;
  refetchSession: () => void;
}
